import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/auth/AuthContext';
import { supabase } from '../../lib/supabaseClient';

interface TeamProfile {
  id: string;
  name: string;
  role: string;
  photo: string | null;
  bio: string | null;
  is_active: boolean;
}

const CrewProfile: React.FC = () => {
  const { userProfile } = useAuth();
  const [teamProfile, setTeamProfile] = useState<TeamProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (userProfile) {
      fetchTeamProfile();
    }
  }, [userProfile]);

  const fetchTeamProfile = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data, error: teamError } = await supabase
        .from('team')
        .select('*')
        .eq('user_id', userProfile?.id)
        .single();

      if (teamError || !data) {
        throw new Error('Team profile not found');
      }

      setTeamProfile(data);
    } catch (err: any) {
      console.error('Error fetching team profile:', err);
      setError(err.message || 'Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-[#B2D8C5] border-t-[#3A5E63] rounded-full animate-spin mb-4"></div>
          <p className="text-[#344854]">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-[#344854] mb-6">My Profile</h1>

        {teamProfile && (
          <div className="space-y-6">
            {/* Profile Photo */}
            {teamProfile.photo && (
              <div className="flex justify-center">
                <img
                  src={teamProfile.photo}
                  alt={teamProfile.name}
                  className="w-32 h-32 rounded-full object-cover border-4 border-[#B2D8C5]"
                />
              </div>
            )}

            {/* Profile Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <div className="bg-gray-50 border border-gray-300 rounded-md px-4 py-2">
                  {teamProfile.name}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <div className="bg-gray-50 border border-gray-300 rounded-md px-4 py-2">
                  {teamProfile.role}
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <div className="bg-gray-50 border border-gray-300 rounded-md px-4 py-2">
                  {userProfile?.email}
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bio
                </label>
                <div className="bg-gray-50 border border-gray-300 rounded-md px-4 py-2 min-h-[100px]">
                  {teamProfile.bio || 'No bio provided'}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <div>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    teamProfile.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {teamProfile.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800">
                💡 To update your profile information, please contact your administrator.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Account Information */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-[#344854] mb-4">Account Information</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account Status
            </label>
            <div className="bg-gray-50 border border-gray-300 rounded-md px-4 py-2">
              {userProfile?.account_status || 'Active'}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Member Since
            </label>
            <div className="bg-gray-50 border border-gray-300 rounded-md px-4 py-2">
              {userProfile?.created_at
                ? new Date(userProfile.created_at).toLocaleDateString()
                : 'N/A'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrewProfile;
